/**
 * CaseReplay — Increment C: press-play rebuild of the case in run order.
 *
 * Isolation: uses `_replayStep` + `.cs-replayhidden` (not FusionStore ingestion step).
 * Scrubbing steps cascade-hides later nodes (rollback) and reveals them when you
 * step forward again. Journal "Up to here" / "From here" drive the same cursor.
 */
import { useEffect } from 'react';
import { X, RotateCcw } from 'lucide-react';
import type cytoscape from 'cytoscape';
import { TimelineStepperControls } from '../../TimelineStepperControls';
import { usePlaybackTimer } from '../../../hooks/usePlaybackTimer';
import { useIntakeStore, type ReplayEntry } from '../../../stores/useIntakeStore';

const cyc = (): cytoscape.Core | undefined => {
  const c = (window as unknown as { __fusionCy?: cytoscape.Core }).__fusionCy;
  return c && !c.destroyed() ? c : undefined;
};

function matchNode(cy: cytoscape.Core, e: ReplayEntry): cytoscape.NodeSingular | null {
  for (const id of [e.targetNodeId, e.nodeLabel].filter(Boolean) as string[]) {
    const n = cy.getElementById(id);
    if (n && n.length) return n as cytoscape.NodeSingular;
  }
  if (e.nodeLabel) {
    const byLabel = cy.nodes().filter((n) => n.data('label') === e.nodeLabel);
    if (byLabel.length) return byLabel[0] as cytoscape.NodeSingular;
  }
  return null;
}

function setupReplaySteps(events: ReplayEntry[]) {
  const cy = cyc(); if (!cy) return;
  cy.nodes().forEach((n) => { n.removeData('_replayStep'); });
  events.forEach((e, i) => {
    const n = matchNode(cy, e);
    if (n && n.data('_replayStep') == null) n.data('_replayStep', i + 1);
  });
}

function applyReplayStep(events: ReplayEntry[], step: number) {
  const cy = cyc(); if (!cy) return;
  cy.batch(() => {
    cy.nodes().forEach((n) => {
      const rs = n.data('_replayStep');
      // Nodes stamped with a later run index are rolled back (hidden).
      // Unstamped nodes (pre-existing seed / context) stay visible.
      if (rs != null && rs > step) n.addClass('cs-replayhidden');
      else n.removeClass('cs-replayhidden');
    });
    cy.edges().forEach((edge) => {
      const hidden = edge.source().hasClass('cs-replayhidden') || edge.target().hasClass('cs-replayhidden');
      if (hidden) edge.addClass('cs-replayhidden');
      else edge.removeClass('cs-replayhidden');
    });
  });
  const cur = events[Math.min(step, events.length) - 1];
  const n = cur ? matchNode(cy, cur) : null;
  if (n && n.length) cy.animate({ center: { eles: n } }, { duration: 240 });
}

function clearReplayGating() {
  const cy = cyc(); if (!cy) return;
  cy.batch(() => cy.elements().removeClass('cs-replayhidden'));
  cy.nodes().forEach((n) => { n.removeData('_replayStep'); });
}

export function CaseReplay() {
  const replay = useIntakeStore((s) => s.replay);
  const setStep = useIntakeStore((s) => s.setReplayStep);
  const setPlaying = useIntakeStore((s) => s.setReplayPlaying);
  const stop = useIntakeStore((s) => s.stopReplay);

  const events = replay?.events ?? [];
  const total = events.length;
  const step = replay?.step ?? 0;
  const playing = replay?.playing ?? false;

  useEffect(() => {
    if (!replay) return;
    setupReplaySteps(events);
    return () => clearReplayGating();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replay?.events]);

  useEffect(() => {
    if (!replay) return;
    applyReplayStep(events, step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, replay?.events]);

  usePlaybackTimer(playing, step, total, setStep, () => setPlaying(false), 1100);

  if (!replay || total === 0) return null;
  const cur = events[Math.min(step, total) - 1];
  const hiddenAfter = Math.max(0, total - step);

  return (
    <div
      className="absolute bottom-3 left-3 right-3 z-30 rounded-xl border border-primary/30 bg-card/95 backdrop-blur shadow-lg px-3 py-2"
      data-tour="fusion-case-replay"
      role="region"
      aria-label="Case replay"
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] uppercase tracking-wide text-primary font-semibold shrink-0">
            Case replay
          </span>
          <span className="text-[11px] font-medium truncate">{cur?.title}</span>
          {cur?.summary && (
            <span className="text-[10px] text-muted-foreground truncate">— {cur.summary}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {hiddenAfter > 0 && (
            <span className="text-[10px] text-muted-foreground tabular-nums hidden sm:inline">
              {hiddenAfter} later run{hiddenAfter === 1 ? '' : 's'} rolled back
            </span>
          )}
          <button
            type="button"
            onClick={() => { setStep(1); setPlaying(true); }}
            title="Restart from first run"
            className="p-1 rounded hover:bg-secondary text-muted-foreground"
          >
            <RotateCcw size={14} />
          </button>
          <button
            type="button"
            onClick={stop}
            title="Exit replay — show full graph again"
            className="p-1 rounded hover:bg-secondary text-muted-foreground"
          >
            <X size={14} />
          </button>
        </div>
      </div>
      <TimelineStepperControls
        currentStep={step}
        totalSteps={total}
        isPlaying={playing}
        onPlay={() => (step >= total ? (setStep(1), setPlaying(true)) : setPlaying(true))}
        onPause={() => setPlaying(false)}
        onRestart={() => { setStep(1); setPlaying(true); }}
        onStepChange={(n) => { setPlaying(false); setStep(n); }}
        stepLabels={events.map((e) => e.title)}
        stepSubs={events.map((e) => e.summary)}
        playLabel={playing ? 'Pause' : step >= total ? 'Replay' : 'Play'}
      />
    </div>
  );
}
